//Library Imports
import { Popover } from "antd";
import { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
//Local Imports
import AddMore from "./AddMore";
import PopoverContent from "./PopoverContent";
import ExclamationDiamond from "assets/img/icons/ExclamationDiamond.svg";
import { WFStatus, WFStatusEnum, ControlNameEnum } from "../interfaces";
import { RootState } from "store";
//Style Imports
import { ItemTypes } from "modules/Common/Components/ItemTypes";
import styled from "styled-components";

type Props = {
  conditionLabel: string;
  conditionRefrence: string;
  childrenLeft: object;
  childrenRight: object;
  disabled: boolean;
  id: string;
  data: object;
  children: string[];
};
const ConditionNode = (props: Props) => {
  const { conditionLabel, conditionRefrence, childrenLeft, childrenRight, disabled, id, data, children } = props;

  const [width, setWidth] = useState(0);
  const [widthLeft, setWidthLeft] = useState(0);
  const [widthRight, setWidthRight] = useState(0);

  const ref = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  // Redux state
  const workflowStatus: WFStatus = useSelector((state: RootState) => state.automation.workflow?.detail?.status);

  const isStatusNotDraft = workflowStatus !== WFStatusEnum.DRAFT;

  // the following code will use in future
  // const [{ transform }, dragRef] = useDrag(
  //   () => ({
  //     type: ItemTypes.CARD,
  //     item: { controlName: conditionLabel, nodeTitle: conditionRefrence, ...data, nodeId: id },
  //   }),
  //   []
  // );

  useLayoutEffect(() => {
    setWidth(ref.current.offsetWidth);
    setWidthLeft(leftRef.current.offsetWidth);
    setWidthRight(rightRef.current.offsetWidth);
  }); // remove [] because it only run on the first render but we have to recalculate it after a new node is created in the workflow

  return (
    <Wrapper>
      <div className="uperline"></div>
      <div className="d-block">
        <Popover
          placement="topRight"
          content={<PopoverContent
            id={id}
            isCondition
            canCopy={false}
            controlName={ControlNameEnum.IF_ELSE}
          />}
          getPopupContainer={() => document.querySelectorAll(".workflow-container")[0] as HTMLElement}
          // trigger="click"
          overlayClassName="workflow-popover workflow-popover-right"
          {...(isStatusNotDraft && { visible: false })} // if status is 'active' or 'inactive' then don't show the popover
        >
          <div
            className={`contition-wrapper ${disabled ? "disabled" : ""}`}
          // style={{ transform }}
          // ref={dragRef}
          >
            {/* disabled toggle class for disable on drag */}
            <div style={{ display: "flex", justifyContent: "center", lineHeight: "1" }}>
              <img className="caution-icon-in-popup" src={ExclamationDiamond} alt="" />
              <div>
                <div className="contition-label">Does the contact match the following conditions?</div>
                <span className="contition-type">{conditionLabel.charAt(0).toUpperCase() + conditionLabel.slice(1)}</span>
                <span className="contition-label">{conditionRefrence}</span>
                {/* <span className="drag-handle">
                  <img src={DragIcon} alt="" />
                </span> */}
              </div>
            </div>
          </div>
        </Popover>
      </div>
      <div className="double-downline" ref={ref}>
        <div className="left-condition" ref={leftRef}>
          <div className="contition-downline-left">
            <div
              className="left-arm"
              style={{
                right: `${Number(width) / Number(2)}px`,
                width: `${Number(width) / Number(2) - Number(widthLeft) / Number(2)}px`,
              }}
            >
              <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" fill="#5f667e">
                <path
                  d="M 0,100 C 0,0 100,100 100,0"
                  fill="none"
                  stroke="#979797"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                ></path>
              </svg>
            </div>
            <span className="condition-true">Yes</span>
            <div className="add-more-row">
              <AddMore
                id={id}
                disabled={disabled}
                side="left"
                hasChildren={!!children?.[0] && children?.[0] !== ""}
              />
            </div>
          </div>
          <div className="arm-container">{childrenLeft}</div>
        </div>
        <div className="right-condition" ref={rightRef}>
          <div className="contition-downline-right">
            <div
              className="right-arm"
              style={{
                left: `${Number(width) / Number(2)}px`,
                width: `${Number(width) / Number(2) - Number(widthRight) / Number(2)}px`,
              }}
            >
              <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" fill="#5f667e">
                <path
                  d="M 100,100 C 100,0 0,100 0,0"
                  fill="none"
                  stroke="#979797"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                ></path>
              </svg>
            </div>
            <span className="condition-false">No</span>
            <div className="add-more-row">
              <AddMore
                id={id}
                disabled={disabled}
                side="right"
                hasChildren={!!children?.[1]}
              />
            </div>
          </div>
          <div className="arm-container">{childrenRight}</div>
        </div>
      </div>
    </Wrapper>
  );
};

export default ConditionNode;

const Wrapper = styled.div`
  display: table;
  text-align: center;
  margin: 0 auto;
  .contition-wrapper {
    background-color: #f5f5f5;
    border-radius: 8px;
    padding: 14px 20px;
    border: 1px solid #929292;
    display: inline-block;
    margin: 0 auto;
    text-align: left;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      border-radius: 6px;
      padding: 8px 12px;
    }
    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
    &:hover .drag-handle {
      opacity: 1;
    }
    .contition-label {
      color: #7e7e7e;
      font-size: 14px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      white-space: nowrap;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
    }
    .drag-handle {
      position: absolute;
      right: -36px;
      top: 50%;
      transform: translateY(-50%);
      cursor: grab;
      transition: 0.5s;
      opacity: 0;
      img {
        width: 16px;
        height: 16px;
      }
    }
    span {
      color: #7e7e7e;
      font-size: 14px;
      font-family: "Roboto", sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
      &.contition-type {
        font-weight: 700;
        /* margin-right: 5px; */
        color: #000000;
      }
      &.contition-label {
        font-weight: 500;
      }
    }
  }
  .caution-icon-in-popup {
    margin-right: 8px;
  }
  .double-downline {
    display: inline-flex;
    position: relative;
    padding-top: 70px;
    .left-condition,
    .right-condition {
      min-width: 470px;
      .contition-downline-left,
      .contition-downline-right {
        width: 50%;
        .left-arm,
        .right-arm {
          height: 70px;
          position: absolute;
          top: 0;
          &:after {
            content: "";
            width: 8px;
            height: 8px;
            border-radius: 10px;
            background-color: #979797;
            display: inline-block;
            position: absolute;
            bottom: -3px;
          }
        }
        .condition-false,
        .condition-true {
          font-size: 16px;
          font-weight: 700;
          font-family: "Roboto", sans-serif;
          &:after {
            content: "";
            width: 2px;
            height: 20px;
            background-color: #979797;
            display: inline-block;
          }
        }
        .add-more-row {
          clear: both;
        }
      }
    }
    .left-condition {
      .contition-downline-left {
        float: right;
        .left-arm {
          &:after {
            left: -3px;
          }
        }
        .condition-true {
          color: #27ce0a;
          float: left;
          margin-left: -14px;
          &:after {
            float: left;
            margin: 24px -14px 0px 13px;
          }
        }
        .add-more-row {
          text-align: left;
          margin-left: -14px;
        }
      }
    }
    .right-condition {
      .contition-downline-right {
        float: left;
        .right-arm {
          &:after {
            right: -3px;
          }
        }
        .condition-false {
          color: #e41010;
          float: right;
          margin-right: -13px;
          &:after {
            margin: 24px 13px 0px -13px;
            float: right;
          }
        }
        .add-more-row {
          text-align: right;
          margin-right: -12px;
        }
      }
    }
  }
  .uperline {
    background-color: #979797;
    width: 2px;
    height: 30px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
    clear: both;
    &:after {
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 10px;
      background-color: #979797;
      display: inline-block;
      position: absolute;
      bottom: -3px;
      left: -3px;
    }
  }
`;
